<template>
  <div class="registration-page">
    <!-- Header Section -->
    <header class="dashboard-header">
      <div class="header-content">
        <h1>Admin & Management Registration</h1>
        <p class="subtitle">Register new admin and management staff</p>
      </div>
    </header>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
      <button 
        :class="['tab-button', { active: activeTab === 'register' }]"
        @click="activeTab = 'register'"
      >
        <i class="fa-solid fa-user-plus"></i>
        Register User
      </button>
      <button 
        :class="['tab-button', { active: activeTab === 'list' }]"
        @click="activeTab = 'list'"
      >
        <i class="fa-solid fa-users"></i>
        Registered Users
      </button>
    </div>

    <!-- Main Content -->
    <div class="registration-container">
      <!-- Registration Form Tab -->
      <div v-if="activeTab === 'register'" class="card">
        <div class="card-header">
          <h2>Registration Form</h2>
        </div>
        
        <div class="card-content">
          <form @submit.prevent="handleSubmit">
            <div class="form-group role-group">
              <label for="role">Role</label>
              <select
                id="role"
                v-model="formData.role"
                required
              >
                <option value="">Select role</option>
                <option value="Admin">Admin</option>
                <option value="Management">Management</option>
                <option value="Finance">Finance</option>
                <option value="Procurement">Procurement</option>
                <option value="Person-In-Charge">Person-In-Charge</option>
              </select>
            </div>

            <div class="form-group subsidiary-group">
              <label for="subsidiary">Subsidiary</label>
              <select
                id="subsidiary"
                v-model="formData.subsidiary"
                required
                :disabled="formData.role === 'Management' || formData.role === 'Procurement'"
              >
                <option value="">Select subsidiary</option>
                <option v-for="subsidiary in subsidiaries" :key="subsidiary.id" :value="subsidiary.id">
                  {{ subsidiary.name }}
                </option>
              </select>
            </div>

            <div class="form-group branch-group" v-if="formData.role === 'Person-In-Charge'">
              <label for="branch">Branch</label>
              <select
                id="branch"
                v-model="formData.branch"
                required
              >
                <option value="">Select branch</option>
                <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                  {{ branch.name }}
                </option>
              </select>
            </div>

            <div class="form-grid">
              <div class="form-group">
                <label for="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  v-model="formData.fullName"
                  required
                  placeholder="Enter full name"
                />
              </div>

              <div class="form-group">
                <label for="phone">Phone Number <span class="optional">(optional)</span></label>
                <input
                  type="tel"
                  id="phone"
                  v-model="formData.phoneNumber"
                  placeholder="Enter phone number"
                />
              </div>

              <div class="form-group">
                <label for="staffNumber">Staff Number</label>
                <input
                  type="text"
                  id="staffNumber"
                  v-model="formData.staffNumber"
                  required
                  placeholder="Enter staff number"
                />
              </div>

              <div class="form-group">
                <label for="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  v-model="formData.email"
                  required
                  placeholder="Enter email address"
                />
              </div>

              <div class="form-group">
                <label for="password">Password</label>
                <div class="password-input">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    id="password"
                    v-model="formData.password"
                    required
                    placeholder="Enter password"
                  />
                  <button 
                    type="button" 
                    class="toggle-password"
                    @click="showPassword = !showPassword"
                  >
                    <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label for="confirmPassword">Confirm Password</label>
                <div class="password-input">
                  <input
                    :type="showConfirmPassword ? 'text' : 'password'"
                    id="confirmPassword"
                    v-model="formData.confirmPassword"
                    required
                    placeholder="Confirm password"
                  />
                  <button 
                    type="button" 
                    class="toggle-password"
                    @click="showConfirmPassword = !showConfirmPassword"
                  >
                    <i :class="showConfirmPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="form-actions">
              <button
                type="submit"
                class="btn-primary"
                :disabled="isLoading"
              >
                {{ isLoading ? 'Registering...' : 'Register User' }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- User List Tab -->
      <div v-if="activeTab === 'list'" class="card">
        <div class="card-header">
          <h2>Registered Users</h2>
        </div>
        
        <div class="card-content">
          <!-- Search and Filter Controls -->
          <div class="search-filter-container">
            <div class="search-section">
              <div class="search-input-group">
                <i class="fa-solid fa-search search-icon"></i>
                <input
                  v-model="searchQuery"
                  @input="resetPaginationOnFilter"
                  type="text"
                  placeholder="Search users by name, email, staff number..."
                  class="search-input"
                />
                <button 
                  v-if="searchQuery"
                  @click="searchQuery = ''; resetPaginationOnFilter()"
                  class="clear-search-btn"
                >
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
            </div>
            
            <div class="filter-section">
              <div class="filter-group">
                <label>Role:</label>
                <select 
                  v-model="filterRole" 
                  @change="resetPaginationOnFilter"
                  class="filter-select"
                >
                  <option value="">All Roles</option>
                  <option value="Admin">Admin</option>
                  <option value="Finance">Finance</option>
                  <option value="Procurement">Procurement</option>
                  <option value="Management">Management</option>
                  <option value="Person-In-Charge">PIC</option>
                </select>
              </div>
              
              <div class="filter-group">
                <label>Subsidiary:</label>
                <select 
                  v-model="filterSubsidiary" 
                  @change="resetPaginationOnFilter"
                  class="filter-select"
                >
                  <option value="">All Subsidiaries</option>
                  <option v-for="subsidiary in subsidiaries" :key="subsidiary.id" :value="subsidiary.id">
                    {{ subsidiary.name }}
                  </option>
                </select>
              </div>
              
              <button 
                v-if="searchQuery || filterRole || filterSubsidiary"
                @click="clearFilters"
                class="clear-filters-btn"
              >
                <i class="fa-solid fa-times"></i>
                Clear Filters
              </button>
            </div>
          </div>

          <div v-if="isLoadingUsers" class="loading-state">
            <i class="fa-solid fa-spinner fa-spin"></i>
            Loading users...
          </div>
          
          <div v-else-if="users.length === 0" class="empty-state">
            <i class="fa-solid fa-users"></i>
            <p>No users found</p>
          </div>
          
          <div v-else-if="sortedUsers.length === 0" class="empty-state">
            <i class="fa-solid fa-search"></i>
            <p>No users match your search criteria</p>
            <button @click="clearFilters" class="btn-primary">Clear Filters</button>
          </div>
          
          <div v-else class="users-table-container">
            <table class="users-table">
              <thead>
                <tr>
                  <th @click="handleSort('name')" class="sortable">
                    Name
                    <i v-if="sortField === 'name'" :class="['sort-icon', sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down']"></i>
                    <i v-else class="sort-icon fa-sort"></i>
                  </th>
                  <th @click="handleSort('email')" class="sortable">
                    Email
                    <i v-if="sortField === 'email'" :class="['sort-icon', sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down']"></i>
                    <i v-else class="sort-icon fa-sort"></i>
                  </th>
                  <th @click="handleSort('role')" class="sortable">
                    Role
                    <i v-if="sortField === 'role'" :class="['sort-icon', sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down']"></i>
                    <i v-else class="sort-icon fa-sort"></i>
                  </th>
                  <th @click="handleSort('subsidiary_name')" class="sortable">
                    Subsidiary
                    <i v-if="sortField === 'subsidiary_name'" :class="['sort-icon', sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down']"></i>
                    <i v-else class="sort-icon fa-sort"></i>
                  </th>
                  <th @click="handleSort('branch_name')" class="sortable">
                    Branch
                    <i v-if="sortField === 'branch_name'" :class="['sort-icon', sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down']"></i>
                    <i v-else class="sort-icon fa-sort"></i>
                  </th>
                  <th @click="handleSort('staff_number')" class="sortable">
                    Staff Number
                    <i v-if="sortField === 'staff_number'" :class="['sort-icon', sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down']"></i>
                    <i v-else class="sort-icon fa-sort"></i>
                  </th>
                  <th @click="handleSort('created_at')" class="sortable">
                    Created Date
                    <i v-if="sortField === 'created_at'" :class="['sort-icon', sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down']"></i>
                    <i v-else class="sort-icon fa-sort"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in paginatedUsers" :key="user.id">
                  <td>{{ user.name }}</td>
                  <td>{{ user.email }}</td>
                  <td>
                    <span :class="['role-badge', user.role.toLowerCase().replace(' ', '-')]">
                      {{ user.role === 'Person-In-Charge' ? 'PIC' : user.role }}
                    </span>
                  </td>
                  <td>{{ user.subsidiary_name || '-' }}</td>
                  <td>{{ user.branch_name || '-' }}</td>
                  <td>{{ user.staff_number || '-' }}</td>
                  <td>{{ formatDate(user.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Pagination Controls -->
          <div v-if="totalPages > 1" class="pagination-container">
            <div class="pagination-info">
              {{ paginationInfo }}
            </div>
            <div class="pagination-controls">
              <button 
                @click="goToPreviousPage" 
                :disabled="currentPage === 1"
                class="pagination-btn"
              >
                <i class="fa-solid fa-chevron-left"></i>
                Previous
              </button>
              
              <div class="page-numbers">
                <button 
                  v-for="page in totalPages" 
                  :key="page"
                  @click="goToPage(page)"
                  :class="['page-btn', { active: currentPage === page }]"
                >
                  {{ page }}
                </button>
              </div>
              
              <button 
                @click="goToNextPage" 
                :disabled="currentPage === totalPages"
                class="pagination-btn"
              >
                Next
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="toast-container" v-if="showToast">
      <div :class="['toast', toastType]">
        <span class="icon" v-if="toastType === 'success'">✓</span>
        {{ toastMessage }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const isLoading = ref(false)
const isLoadingUsers = ref(false)
const showToast = ref(false)
const toastMessage = ref('')
const toastType = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const subsidiaries = ref([])
const branches = ref([])
const users = ref([])
const activeTab = ref('register')
const sortField = ref('created_at')
const sortDirection = ref('desc')
const currentPage = ref(1)
const itemsPerPage = 10
const searchQuery = ref('')
const filterRole = ref('')
const filterSubsidiary = ref('')

const formData = ref({
  role: '',
  subsidiary: '',
  branch: '',
  fullName: '',
  phoneNumber: '',
  staffNumber: '',
  email: '',
  password: '',
  confirmPassword: ''
})

// API instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const showNotification = (message, type = 'success') => {
  toastMessage.value = message
  toastType.value = type
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, 3000)
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const sortedUsers = computed(() => {
  let filteredUsers = [...users.value]
  
  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filteredUsers = filteredUsers.filter(user => 
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      user.staff_number.toLowerCase().includes(query) ||
      (user.subsidiary_name && user.subsidiary_name.toLowerCase().includes(query)) ||
      (user.branch_name && user.branch_name.toLowerCase().includes(query))
    )
  }
  
  // Apply role filter
  if (filterRole.value) {
    filteredUsers = filteredUsers.filter(user => user.role === filterRole.value)
  }
  
  // Apply subsidiary filter
  if (filterSubsidiary.value) {
    filteredUsers = filteredUsers.filter(user => user.subsidiary_id == filterSubsidiary.value)
  }
  
  // Apply sorting
  return filteredUsers.sort((a, b) => {
    let aValue = a[sortField.value]
    let bValue = b[sortField.value]
    
    // Handle null/undefined values
    if (aValue == null) aValue = ''
    if (bValue == null) bValue = ''
    
    // Convert to string for comparison
    aValue = String(aValue).toLowerCase()
    bValue = String(bValue).toLowerCase()
    
    if (sortDirection.value === 'asc') {
      return aValue.localeCompare(bValue)
    } else {
      return bValue.localeCompare(aValue)
    }
  })
})

const paginatedUsers = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return sortedUsers.value.slice(start, end)
})

const totalPages = computed(() => {
  return Math.ceil(sortedUsers.value.length / itemsPerPage)
})

const paginationInfo = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage + 1
  const end = Math.min(currentPage.value * itemsPerPage, sortedUsers.value.length)
  return `${start}-${end} of ${sortedUsers.value.length}`
})

const handleSort = (field) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDirection.value = 'asc'
  }
  // Reset to first page when sorting
  currentPage.value = 1
}

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const goToPreviousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const goToNextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

const clearFilters = () => {
  searchQuery.value = ''
  filterRole.value = ''
  filterSubsidiary.value = ''
  currentPage.value = 1
}

const resetPaginationOnFilter = () => {
  currentPage.value = 1
}

const fetchSubsidiaries = async () => {
  try {
    const response = await api.get('/api/subsidiary')
    subsidiaries.value = response.data
  } catch (error) {
    showNotification('Failed to fetch subsidiaries', 'error')
  }
}

const fetchBranches = async () => {
  try {
    if (!formData.value.subsidiary) {
      branches.value = []
      return
    }
    const response = await api.get(`/api/branches?subsidiary_id=${formData.value.subsidiary}`)
    branches.value = response.data
  } catch (error) {
    showNotification('Failed to fetch branches', 'error')
  }
}

const fetchUsers = async () => {
  try {
    isLoadingUsers.value = true
    
    // Use the new endpoint that allows Finance users to see all users
    const response = await api.get('/api/users/all')
    users.value = response.data
  } catch (error) {
    showNotification('Failed to fetch users', 'error')
  } finally {
    isLoadingUsers.value = false
  }
}

// Watch for subsidiary changes to fetch branches
watch(() => formData.value.subsidiary, (newSubsidiary) => {
  if (newSubsidiary) {
    fetchBranches()
  } else {
    branches.value = []
  }
})

// Watch for tab changes to automatically load users and set default sort
watch(() => activeTab.value, (newTab) => {
  if (newTab === 'list') {
    if (users.value.length === 0) {
      fetchUsers()
    }
    // Set default sort to role when switching to user list tab
    sortField.value = 'role'
    sortDirection.value = 'asc'
  }
})

const validateForm = () => {
  if ((formData.value.role === 'Admin' || formData.value.role === 'Finance') && !formData.value.subsidiary) {
    showNotification('Please select a subsidiary for Admin/Finance role', 'error')
    return false
  }
  if (formData.value.password !== formData.value.confirmPassword) {
    showNotification('Passwords do not match', 'error')
    return false
  }
  if (formData.value.password.length < 6) {
    showNotification('Password must be at least 6 characters long', 'error')
    return false
  }
  if (formData.value.phoneNumber && !formData.value.phoneNumber.match(/^[0-9]{10,11}$/)) {
    showNotification('Please enter a valid phone number (10-11 digits)', 'error')
    return false
  }
  return true
}

const checkPhoneExists = async (phone) => {
  try {
    const response = await api.post('/api/users/check-phone', { phone })
    return response.data.exists
  } catch (error) {
    return false
  }
}

const handleSubmit = async () => {
  if (!validateForm()) return

  try {
    isLoading.value = true
    
    if (formData.value.phoneNumber?.trim()) {
      const phoneExists = await checkPhoneExists(formData.value.phoneNumber)
      if (phoneExists) {
        showNotification('This phone number is already registered', 'error')
        isLoading.value = false
        return
      }
    }

    const response = await api.post('/api/users/register', {
      role: formData.value.role,
      subsidiary: (formData.value.role === 'Management' || formData.value.role === 'Procurement') ? null : formData.value.subsidiary,
      branch: formData.value.role === 'Person-In-Charge' ? formData.value.branch : null,
      name: formData.value.fullName,
      phone: formData.value.phoneNumber?.trim() || null,
      staffNumber: formData.value.staffNumber,
      email: formData.value.email,
      password: formData.value.password
    })

    if (response.data.success) {
      showNotification('User registered successfully')
      formData.value = {
        role: '',
        subsidiary: '',
        branch: '',
        fullName: '',
        phoneNumber: '',
        staffNumber: '',
        email: '',
        password: '',
        confirmPassword: ''
      }
      // Refresh user list if we're on the list tab
      if (activeTab.value === 'list') {
        fetchUsers()
      }
    } else {
      showNotification(response.data.message || 'Failed to register user', 'error')
    }
  } catch (error) {
    if (error.response) {
      showNotification(error.response.data.message || 'Failed to register user', 'error')
    } else if (error.request) {
      showNotification('Could not connect to the server. Please try again later.', 'error')
    } else {
      showNotification('An error occurred. Please try again.', 'error')
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  // Check if user is finance
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    if (user.role.toLowerCase() !== 'finance') {
      router.push('/')
    }
  } else {
    router.push('/')
  }
  
  fetchSubsidiaries()
})
</script>

<style scoped>
.registration-page {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.header-content h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
}

.subtitle {
  font-size: 0.875rem;
  color: #64748b;
}

.tab-navigation {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.tab-button {
  padding: 12px 24px;
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.tab-button:hover:not(.active) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.tab-button.active {
  background: #0ea5e9;
  color: white;
  border-color: #0ea5e9;
}

.tab-button i {
  font-size: 1rem;
}

.registration-container {
  width: 100%;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.card-header {
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.card-content {
  padding: 24px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.form-group {
  margin-bottom: 24px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
}

.optional {
  font-weight: 400;
  color: #94a3b8;
}

input,
select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #1e293b;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

select {
  padding-right: 40px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
}

input:hover {
  border-color: #94a3b8;
}

input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

select:disabled {
  background-color: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
  border-color: #e2e8f0;
  opacity: 0.8;
}

select:hover {
  border-color: #94a3b8;
  background-color: #f8fafc;
}

select:hover:disabled {
  border-color: #e2e8f0;
  background-color: #f1f5f9;
}

select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background-color: white;
}

select option {
  padding: 12px;
  font-size: 0.875rem;
  color: #1e293b;
  background: white;
}

select option[value=""] {
  color: #94a3b8;
}

.form-actions {
  margin-top: 32px;
}

.btn-primary {
  padding: 12px 24px;
  background: #1e40af;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 150px;
  width: 100%;
}

.btn-primary:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.btn-primary:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.role-group,
.subsidiary-group,
.branch-group {
  margin-bottom: 24px;
}

.role-group select,
.subsidiary-group select,
.branch-group select {
  font-weight: 500;
}

.toast-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 1000;
  min-width: 300px;
}

.toast {
  padding: 16px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: slideIn 0.3s ease-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.toast.success {
  background-color: #dcfce7;
  color: #16a34a;
  border-color: rgba(22, 163, 74, 0.1);
}

.toast.error {
  background-color: #fee2e2;
  color: #dc2626;
  border-color: rgba(220, 38, 38, 0.1);
}

.toast .icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: currentColor;
  color: white;
  font-size: 14px;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.password-input {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input input {
  padding-right: 40px;
  width: 100%;
}

.toggle-password {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-password:hover {
  color: #1e293b;
}

.toggle-password i {
  font-size: 1rem;
}

/* User List Styles */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: #64748b;
  text-align: center;
}

.loading-state i,
.empty-state i {
  font-size: 2rem;
  margin-bottom: 16px;
  color: #94a3b8;
}

.empty-state p {
  font-size: 1rem;
  margin: 0;
}

.users-table-container {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  font-size: 0.875rem;
}

.users-table th {
  background: #f8fafc;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.sortable {
  cursor: pointer;
  user-select: none;
  position: relative;
  transition: background-color 0.2s ease;
}

.sortable:hover {
  background: #e2e8f0;
}

.sort-icon {
  margin-left: 8px;
  font-size: 0.75rem;
  color: #64748b;
}

.sortable:hover .sort-icon {
  color: #374151;
}

.users-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
  vertical-align: middle;
}

.users-table tbody tr:hover {
  background: #f8fafc;
}

.users-table tbody tr:last-child td {
  border-bottom: none;
}

.role-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.role-badge.admin {
  background: #fef3c7;
  color: #92400e;
}

.role-badge.management {
  background: #dbeafe;
  color: #1e40af;
}

.role-badge.finance {
  background: #dcfce7;
  color: #16a34a;
}

.role-badge.person-in-charge {
  background: #f3e8ff;
  color: #7c3aed;
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge.active {
  background: #dcfce7;
  color: #16a34a;
}

.status-badge.inactive {
  background: #fee2e2;
  color: #dc2626;
}

/* Pagination Styles */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 16px 0;
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  font-size: 0.875rem;
  color: #64748b;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  padding: 8px 16px;
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pagination-btn:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-btn {
  padding: 8px 12px;
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
}

.page-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

.page-btn.active {
  background: #0ea5e9;
  color: white;
  border-color: #0ea5e9;
}

/* Search and Filter Styles */
.search-filter-container {
  margin-bottom: 24px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.search-section {
  margin-bottom: 16px;
}

.search-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #64748b;
  font-size: 0.875rem;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 12px 40px 12px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.clear-search-btn {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.clear-search-btn:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.filter-section {
  display: flex;
  gap: 16px;
  align-items: end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  font-size: 0.875rem;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 150px;
}

.filter-select:hover {
  border-color: #cbd5e1;
}

.filter-select:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.clear-filters-btn {
  padding: 8px 16px;
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.clear-filters-btn:hover {
  background: #fecaca;
  border-color: #fca5a5;
}

@media (max-width: 768px) {
  .registration-page {
    padding: 16px;
  }

  .dashboard-header {
    margin-bottom: 24px;
  }

  .header-content h1 {
    font-size: 1.5rem;
  }

  .tab-navigation {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
  }

  .tab-button {
    width: 100%;
    justify-content: center;
    padding: 10px 16px;
    font-size: 0.8rem;
  }

  .users-table-container {
    font-size: 0.8rem;
  }

  .users-table th,
  .users-table td {
    padding: 8px 12px;
  }

  .pagination-container {
    flex-direction: column;
    gap: 16px;
    align-items: center;
  }

  .pagination-controls {
    flex-wrap: wrap;
    justify-content: center;
  }

  .page-numbers {
    order: -1;
  }

  .search-filter-container {
    padding: 16px;
  }

  .filter-section {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .filter-group {
    width: 100%;
  }

  .filter-select {
    min-width: auto;
    width: 100%;
  }

  .clear-filters-btn {
    width: 100%;
    justify-content: center;
  }

  .card-header {
    padding: 16px;
  }

  .card-content {
    padding: 16px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .role-group,
  .subsidiary-group,
  .branch-group {
    margin-bottom: 16px;
  }

  input,
  select {
    padding: 10px 14px;
    font-size: 16px; /* Prevent zoom on iOS */
  }

  .btn-primary {
    padding: 12px 20px;
    font-size: 1rem;
    width: 100%;
  }

  .toast-container {
    left: 16px;
    right: 16px;
    min-width: auto;
  }

  .toast {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .registration-page {
    padding: 12px;
  }

  .header-content h1 {
    font-size: 1.25rem;
  }

  .card-header h2 {
    font-size: 1.125rem;
  }

  .form-actions {
    margin-top: 24px;
  }
}
</style> 